export type TemplateObjectValue = '' | TemplateObject | TemplateObjectValue[];

export type TemplateObject = Record<string, TemplateObjectValue>;

export interface TemplateCommand {
  type: string;
  code: string;
}

interface LoopContext {
  varName: string;
  path: string[];
}

const propertyPathPattern = /^[a-zA-Z_$][\w$]*(\.[a-zA-Z_$][\w$]*)*$/;
const loopPattern = /^\s*([a-zA-Z_$][\w$]*)\s+IN\s+(.+)\s*$/i;
const loopVariablePattern = /^\$([a-zA-Z_$][\w$]*)(\.[a-zA-Z_$][\w$]*)*$/;

function setDeep(object: TemplateObject, path: string[], value: TemplateObjectValue) {
  let current = object as Record<string, TemplateObjectValue>;

  for (let index = 0; index < path.length - 1; index += 1) {
    const key = path[index];
    if (current[key] === undefined) current[key] = {};
    current = current[key] as TemplateObject;
  }

  const lastKey = path[path.length - 1];
  if (lastKey && current[lastKey] === undefined) current[lastKey] = value;
}

function ensureArrayAtPath(
  object: TemplateObject,
  path: string[],
  itemFactory: () => TemplateObjectValue,
) {
  let current = object as Record<string, TemplateObjectValue>;

  for (let index = 0; index < path.length - 1; index += 1) {
    const key = path[index];
    if (current[key] === undefined) current[key] = {};
    current = current[key] as TemplateObject;
  }

  const lastKey = path[path.length - 1];
  if (lastKey && !Array.isArray(current[lastKey])) current[lastKey] = [itemFactory()];
}

function resolveExpressionPath(expression: string, loopStack: LoopContext[]): string[] | undefined {
  const trimmedExpression = expression.trim();

  if (!trimmedExpression.startsWith('$')) {
    return propertyPathPattern.test(trimmedExpression) ? trimmedExpression.split('.') : undefined;
  }

  const match = trimmedExpression.match(loopVariablePattern);
  if (!match) return undefined;

  const variableName = match[1];
  const loop = [...loopStack].reverse().find((entry) => entry.varName === variableName);
  if (!loop) return undefined;

  const suffix = trimmedExpression.slice(variableName.length + 1);
  return [...loop.path, ...suffix.split('.').filter(Boolean)];
}

function inferLoopItem(
  commands: TemplateCommand[],
  startIndex: number,
  loopVariable: string,
): TemplateObjectValue {
  let hasNestedFields = false;
  const nestedObject: TemplateObject = {};

  for (let index = startIndex + 1; index < commands.length; index += 1) {
    const command = commands[index];

    if (command.type === 'END-FOR' && command.code.trim() === loopVariable) break;

    if (command.type === 'INS') {
      const code = command.code.trim();
      if (code === `$${loopVariable}`) continue;

      const prefix = `$${loopVariable}.`;
      if (code.startsWith(prefix)) {
        hasNestedFields = true;
        const propertyPath = code.slice(prefix.length);
        if (propertyPathPattern.test(propertyPath)) {
          setDeep(nestedObject, propertyPath.split('.'), '');
        }
      }
    }

    if (command.type === 'FOR') {
      const match = command.code.match(loopPattern);
      if (!match) continue;

      const sourceExpression = match[2].trim();
      const prefix = `$${loopVariable}.`;
      if (sourceExpression.startsWith(prefix)) {
        hasNestedFields = true;
        const propertyPath = sourceExpression.slice(prefix.length);
        if (propertyPathPattern.test(propertyPath)) {
          setDeep(nestedObject, propertyPath.split('.'), ['']);
        }
      }
    }
  }

  return hasNestedFields ? nestedObject : '';
}

/**
 * Builds example JSON data from the template commands emitted by docx-templates.
 * This matches the object-extraction behavior used by pritset.templating.
 */
export class TemplateObjectUtils {
  static buildObjectFromCommands(commands: TemplateCommand[]): TemplateObject {
    const result: TemplateObject = {};
    const loopStack: LoopContext[] = [];

    commands.forEach((command, index) => {
      const code = command.code.trim();

      if (command.type === 'INS') {
        const path = resolveExpressionPath(code, loopStack);
        if (path) setDeep(result, path, '');
        return;
      }

      if (command.type === 'IMAGE') {
        const matches = [...code.matchAll(/\b([a-zA-Z_$][\w$]*(?:\.[a-zA-Z_$][\w$]*)+)\b/g)];
        matches.forEach((match) => {
          const path = resolveExpressionPath(match[1], loopStack);
          if (path) setDeep(result, path, '');
        });
        return;
      }

      if (command.type === 'FOR') {
        const match = code.match(loopPattern);
        if (!match) return;

        const loopVariable = match[1];
        const arrayPath = resolveExpressionPath(match[2], loopStack);
        if (!arrayPath) return;

        ensureArrayAtPath(result, arrayPath, () => inferLoopItem(commands, index, loopVariable));
        loopStack.push({
          varName: loopVariable,
          path: arrayPath.concat('0').slice(0, -1),
        });
        return;
      }

      if (command.type === 'END-FOR') loopStack.pop();
    });

    return JSON.parse(JSON.stringify(result)) as TemplateObject;
  }
}
